import { TherapeuticArea } from '@lib/types/TherapeuticArea'
import {
  ALZHEIMER_TEST_TRIAL,
  PARKINSON_TEST_TRIAL,
} from '@modules/trials/constants/trials'

export const STRUCTURED_QUESTION_UNSURE = 'Not Sure'
export const STRUCTURED_QUESTION_NONE = 'None'

export const CommonComorbidityNames: string[] = [
  'Depression',
  'Anxiety',
  'Schizophrenia',
  'Bipolar Disorder',
  'Substance Abuse',
  'Obsessive-Compulsive Disorder',
  'Body Dysmorphic Disorder',
  'Premenstrual Dysphoric Disorder',
  'Agoraphobia',
  'Social Anxiety Disorder',
  'Post-Traumatic Stress Disorder',
]

type ComorbiditiesByTrial = {
  comorbidities: string[]
  nctIds: string[]
}

type ComorbiditiesByCondition = {
  comorbidities: string[]
  conditions: string[]
  therapeuticAreas: string[]
}

export const CommonComorbidityNamesByNctId: ComorbiditiesByTrial[] = [
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Hypertension',
      'Coronary Artery Disease',
      'Atrial Fibrillation',
      'Type 2 Diabetes',
      'Heart Disease',
      "Parkinson's Disease",
      'Osteoporosis',
      'Hypothyroidism',
    ],
    // Alzheimer's Disease
    nctIds: [ALZHEIMER_TEST_TRIAL.nctId],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Breast Cancer',
      'Cancer',
      'Bipolar Disorder',
      'Schizophrenia',
      'Type 1 Diabetes',
      'Type 2 Diabetes',
      'Heart Failure',
    ],
    // Parkinson's Disease
    nctIds: [PARKINSON_TEST_TRIAL.nctId],
  },
]

// Followed list in https://docs.google.com/spreadsheets/d/1BdRKefz_lhRhctVxVNcusNVvIw7fupgM8q-0ZGId088/edit?gid=268586133#gid=268586133
export const CommonComorbidityNamesByCondition: ComorbiditiesByCondition[] = [
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Schizophrenia',
      'Bipolar Disorder',
      'Substance Abuse',
      'Obsessive-Compulsive Disorder',
      'Body Dysmorphic Disorder',
      'Premenstrual Dysphoric Disorder',
      'Agoraphobia',
      'Social Anxiety Disorder',
      'Post-Traumatic Stress Disorder',
    ],
    conditions: [],
    therapeuticAreas: [TherapeuticArea.Psychiatry],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Chronic obstructive pulmonary disease',
      'cystic fibrosis',
      'pulmonary fibrosis',
      'interstitial lung disease',
      'sarcoidosis',
      'lupus',
      'rheumatoid arthritis',
    ],
    conditions: ['asthma'],
    therapeuticAreas: [TherapeuticArea.Respirology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Substance Abuse',
      'Cirrhosis',
      'Chronic liver disease',
      'Chronic kidney disease',
      'Bipolar',
      'Dementia',
    ],
    conditions: ['epilepsy'],
    therapeuticAreas: [TherapeuticArea.Neurology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      "Crohn's Disease",
      'Ulcerative colitis',
      'Rheumatoid Arthritis',
      'Systemic lupus erythematosus',
      'Gout',
      'Multiple sclerosis',
      'Skin Cancer',
    ],
    conditions: ['psoriatic arthritis'],
    therapeuticAreas: [TherapeuticArea.Rheumatology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      "Crohn's Disease",
      'Ulcerative colitis',
      'Rheumatoid Arthritis',
      'Systemic lupus erythematosus',
      'Gout',
      'Multiple sclerosis',
      'Skin Cancer',
    ],
    conditions: ['plaque psoriasis'],
    therapeuticAreas: [TherapeuticArea.Dermatology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Rheumatoid arthritis',
      'psoriatic arthritis',
      'Gout',
      'Fibromyalgia',
      'Chronic kidney disease',
    ],
    conditions: ['osteoarthritis'],
    therapeuticAreas: [TherapeuticArea.Orthopedics],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Chronic Kidney Disease',
      'Liver Disease',
      'thyroid disease',
      'Systemic Lupus Erythematosus',
      'Rheumatoid Arthritis',
    ],
    conditions: ['myasthenia gravis'],
    therapeuticAreas: [TherapeuticArea.Neurology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Glaucoma',
      'Diabetic eye disease',
      'macular degeneration',
      'uveitis',
      'retinal thinning',
      'Heart disease',
    ],
    conditions: ['stargardt disease'],
    therapeuticAreas: [TherapeuticArea.Ophthalmology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Osteomyelitis',
      'Septic arthritis',
      'Hepatitis B',
      'Hepatitis C',
      'Chronic Kidney Disease',
      'Liver Disease',
      'Multiple Sclerosis',
      'Interstitial lung disease',
      'Heart Disease',
    ],
    conditions: ['rheumatoid arthritis'],
    therapeuticAreas: [TherapeuticArea.Rheumatology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Liver Disease',
      'Chronic Kidney Disease',
      "Crohn's",
      'indeterminate colitis',
      'Depression',
      'Substance Abuse',
      'Chronic hepatitis B',
      'Hepatitis C',
    ],
    conditions: ['ulcerative colitis'],
    therapeuticAreas: [TherapeuticArea.Gastroenterology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      "Sjögren's syndrome",
      'Rheumatoid arthritis',
      'Blepharitis',
      'Meibomian-gland dysfunction',
      'Allergic conjunctivitis',
      'Chronic ocular allergy',
      'Glaucoma',
      'Herpes simplex keratitis',
      'Thyroid eye disease',
    ],
    conditions: ['dry eye'],
    therapeuticAreas: [TherapeuticArea.Ophthalmology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Heart Attack (acute myocardial infarction)',
      'Stroke',
      'Unstable angina',
      'Congestive heart failure',
      'Type 1 Diabetes',
      'Type 2 Diabetes',
      'Chronic Kidney Disease',
      'Pancreatitis',
    ],
    conditions: ['diabetes'],
    therapeuticAreas: [TherapeuticArea.MetabolismAndEndocrinology],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Uncontrolled hypertension',
      'Heart Attack (acute myocardial infarction)',
      'Stroke',
      'Unstable angina',
      'Congestive heart failure',
      'Type 1 Diabetes',
      'Chronic Kidney Disease',
      'Pancreatitis',
      'Liver Disease',
    ],
    conditions: ['obesity'],
    therapeuticAreas: [
      TherapeuticArea.Gastroenterology,
      TherapeuticArea.MetabolismAndEndocrinology,
    ],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Heart Attack (acute myocardial infarction)',
      'Stroke',
      'Unstable angina',
      'Congestive heart failure',
      'Type 1 Diabetes',
      'Chronic Kidney Disease',
      'Pancreatitis',
      'Liver Disease',
      'Substance Abuse',
    ],
    conditions: ['hypertension'],
    therapeuticAreas: [TherapeuticArea.Cardiology],
  },
  {
    comorbidities: [
      'Type 1 Diabetes',
      'Type 2 Diabetes',
      'HIV',
      'Hepatitis B',
      'Hepatitis C',
      'Multiple Sclerosis',
      'Asthma',
      'Epilepsy',
      'Seizures',
      'High Blood Pressure',
    ],
    conditions: ['depression', 'anxiety', 'bipolar disorder'],
    therapeuticAreas: [],
  },
  {
    comorbidities: [
      'Depression',
      'Anxiety',
      'Substance Abuse',
      'Chronic Liver Disease',
      'Chronic Kidney Disease',
      'Bipolar Disorder',
      'Thyroid Disease',
      'Lupus',
      'Dementia',
    ],
    conditions: ['multiple sclerosis'],
    therapeuticAreas: [],
  },
]

export const PsychiatricComorbidityNamesByCondition: Omit<
  ComorbiditiesByCondition,
  'therapeuticAreas'
>[] = [
  {
    comorbidities: [
      'Obsessive Compulsive Disorder',
      'Post-traumatic stress Disorder',
      'Attention Deficit Hyperactivity Disorder',
      'Schizophrenia',
      'Social Anxiety Disorder',
      'Borderline Personality Disorder',
      'Substance Abuse',
    ],
    conditions: ['depression', 'anxiety', 'bipolar disorder'],
  },
  {
    comorbidities: ['Depression', 'Bipolar Disorder'],
    conditions: ['anxiety'],
  },
  {
    comorbidities: ['Anxiety', 'Bipolar Disorder'],
    conditions: ['depression'],
  },
  {
    comorbidities: ['Anxiety', 'Depression'],
    conditions: ['bipolar disorder'],
  },
]
